import {Feature} from 'geojson';
import {AnyAction} from 'redux';
import {FrameActions} from '@mc/react-frame';
import {FeatureIdType, FeatureZoom} from '../enums';
import {FeaturePointer, PaddingOptions, ZoomOptions} from '../types';

/**
 * Parametry k ukazatelům na features.
 */
export interface FeaturePointerOptions {
    /**
     * Typ ID features v ukazatelích.
     */
    featureIdType?: FeatureIdType;
}

/**
 * Základní parametry zoomu na features.
 *
 * Alias pro {@link ZoomOptions} zachovaný z důvodu zpětné kompatibility.
 */
export type FeatureZoomBaseOptions = ZoomOptions;

/**
 * Parametry zoomu na features.
 */
export interface FeatureZoomOptions extends FeatureZoomBaseOptions {
    /**
     * Způsob jakým provést zoom na načtené features.
     * Hodnota true má stejný význam jako konstanta {@link FeatureZoom.CENTER_AND_ZOOM}
     */
    zoom?: boolean | FeatureZoom;
}

/**
 * Parametry centrování na features.
 */
export interface FeatureCenterOptions {
    /**
     * Příznak, zda cílovou pozici nastavit jako počáteční střed mapy.
     */
    useAsOrigin?: boolean;
}

/**
 * Parametry načítání features.
 */
export interface FeatureLoadOptions {
    /**
     * Zda donačíst chybějících features a vrstvy do mapy.
     */
    loadMissing?: boolean;
}

/**
 * Parametry restrikce features.
 */
export interface FeatureRestrictOptions {
    /**
     * Zpráva popisující restrikci.
     */
    description?: string;
}

/**
 * Parametry zobrazení detailu features.
 */
export interface FeatureDetailOptions {
    /**
     * Zda zobrazit detail předaných features, které jsou z datových vrstev.
     */
    showDetail?: boolean;
}

/**
 * Parametry slučování features.
 */
export interface FeatureMergeOptions {
    /**
     * Zda sloučit nové features s těmi aktuálními.
     *
     * - `false` (výchozí) - Nové features nahradí ty existující.
     * - `true` - Nové features budou přidány k existujícím, pouze features s identickým ID jsou nahrazeny.
     */
    merge?: true;
}

/**
 * Akce features.
 */
const FeatureActions = {
    /**
     * Načte features do "vkládací" vrstvy editoru.
     *
     * @param area - Oblast store.souřadnicový referenční systém
     * @param features - Seznam features pro načtení.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      zoom: FeatureZoom.CENTER_AND_ZOOM
     *                  }.
     * @returns Akce.
     */
    loadFeatures(
        area: string,
        features: Feature[],
        options: FeatureZoomOptions & FeatureCenterOptions & FeatureMergeOptions = {},
    ): AnyAction {
        const {
            zoom = FeatureZoom.CENTER_AND_ZOOM,
            zoomPadding,
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
            padding,
            merge,
        } = options;

        return FrameActions.postMessage(area, {
            type: 'FEATURE_LOAD',
            features,
            zoom,
            zoomPadding: padding || zoomPadding, // zoomPadding je původní jméno, zachováno pro zpětnou kompatibilitu
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
            merge,
        });
    },

    /**
     * Odnačte features ze "vkládací" vrstvy editoru.
     *
     * @param area - Oblast store.souřadnicový referenční systém
     * @param featureIds - ID features pro odnačtení, `undefined` pro odnačtení všeho.
     * @returns Akce.
     */
    unloadFeatures(area: string, featureIds?: string[]): AnyAction {
        return FrameActions.postMessage(area, {
            type: 'FEATURE_UNLOAD',
            featureIds,
        });
    },

    /**
     * Vybere features s danými ID.
     * Předání prázdného seznamu zruší aktuální výběr.
     *
     * @param area - Oblast store.
     * @param featurePointers - Seznam ukazatelů na features pro výběr.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true,
     *                      showDetail: true,
     *                      zoom: FeatureZoom.CENTER_AND_ZOOM
     *                  }.
     * @returns Akce.
     */
    selectFeatures(
        area: string,
        featurePointers: FeaturePointer[],
        options: FeaturePointerOptions &
            FeatureLoadOptions &
            FeatureDetailOptions &
            FeatureZoomOptions &
            FeatureCenterOptions = {},
    ): AnyAction {
        const {
            featureIdType = FeatureIdType.FEATURE_ID,
            loadMissing = true,
            showDetail = true,
            zoom = FeatureZoom.CENTER_AND_ZOOM,
            zoomPadding,
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
            padding,
        } = options;

        // Původně byl druhý parametr pole IDček featureIds, nově je to pole
        // strukturovaných objektů featurePointers. Mapio má zpětnou kompatibilitu
        // a stále rozumí původnímu parametru featureIds.

        if (featurePointers.some(value => typeof value !== 'object')) {
            console.warn(
                '[FeatureActions.selectFeatures] Passing array of feature ID is now deprecated.' +
                    ' Pass array of FeaturePointer objects instead.',
            );

            return FrameActions.postMessage(area, {
                type: 'FEATURE_SELECT',
                featureIds: featurePointers,
                featureIdType,
                loadMissing,
                showDetail,
                zoom,
                zoomPadding: padding || zoomPadding, // zoomPadding je původní jméno, zachováno pro zpětnou kompatibilitu
                zoomRatio,
                zoomMinScale,
                useAsOrigin,
            });
        }

        return FrameActions.postMessage(area, {
            type: 'FEATURE_SELECT',
            featurePointers,
            featureIdType,
            loadMissing,
            showDetail,
            zoom,
            zoomPadding: padding || zoomPadding, // zoomPadding je původní jméno, zachováno pro zpětnou kompatibilitu
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
        });
    },

    /**
     * Omezí viditelnost pouze na features s danými ID.
     *
     * @param area - Oblast store.
     * @param featurePointers - Seznam ukazatelů na features, jež mají zůstat viditelné.
     *                          `null` pro zrušení restrikce.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID
     *                  }.
     * @returns Akce.
     */
    restrictFeatures(
        area: string,
        featurePointers: FeaturePointer[] | null,
        options: FeaturePointerOptions & FeatureRestrictOptions = {},
    ): AnyAction {
        const {featureIdType = FeatureIdType.FEATURE_ID, description} = options;
        return FrameActions.postMessage(area, {
            type: 'FEATURE_RESTRICT',
            featurePointers,
            featureIdType,
            description,
        });
    },

    /**
     * Smaže features s danými ID.
     *
     * @param area - Oblast store.
     * @param featurePointers - Seznam ukazatelů na features pro smazání.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID
     *                  }.
     * @returns Akce.
     */
    deleteFeatures(area: string, featurePointers: FeaturePointer[], options: FeaturePointerOptions = {}): AnyAction {
        const {featureIdType = FeatureIdType.FEATURE_ID} = options;
        return FrameActions.postMessage(area, {
            type: 'FEATURE_DELETE',
            featurePointers,
            featureIdType,
        });
    },

    /**
     * Provede zoom na features s danými ID.
     *
     * @param area - Oblast store.
     * @param featurePointers - Seznam ukazatelů na features pro zoom.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true
     *                  }.
     * @returns Akce.
     */
    zoomOnFeatures(
        area: string,
        featurePointers: FeaturePointer[],
        options: FeaturePointerOptions & FeatureLoadOptions & FeatureCenterOptions & ZoomOptions = {},
    ): AnyAction {
        const {
            featureIdType = FeatureIdType.FEATURE_ID,
            loadMissing = true,
            zoomPadding,
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
            padding,
        } = options;

        return FrameActions.postMessage(area, {
            type: 'FEATURE_ZOOM',
            featurePointers,
            featureIdType,
            loadMissing,
            zoomPadding: padding || zoomPadding, // zoomPadding je původní jméno, zachováno pro zpětnou kompatibilitu
            zoomRatio,
            zoomMinScale,
            useAsOrigin,
        });
    },

    /**
     * Provede vycentrování na features s danými ID.
     *
     * @param area - Oblast store.
     * @param featurePointers - Seznam ukazatelů na features pro vycentrování.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true
     *                  }.
     * @returns Akce.
     */
    centerOnFeatures(
        area: string,
        featurePointers: FeaturePointer[],
        options: FeaturePointerOptions & FeatureLoadOptions & FeatureCenterOptions & PaddingOptions = {},
    ): AnyAction {
        const {featureIdType = FeatureIdType.FEATURE_ID, loadMissing = true, useAsOrigin, padding} = options;

        return FrameActions.postMessage(area, {
            type: 'FEATURE_CENTER',
            featurePointers,
            featureIdType,
            loadMissing,
            useAsOrigin,
            padding,
        });
    },

    /**
     * Pokusí se fulltextově vyhledat feature podle zadaného řetězce. V ideálním případě se najde pouze jedna konkrétní feature.
     * Fulltextové vyhledávání může najít více výsledků, v takovém případě se vezme feature, která je první v řadě.
     * Vyhledaná featuru se vybere. Provede se zoom na danou feature a zobrazení detailních informací o ní.
     *
     * @param area - Oblast store.
     * @param searchText - Řetězec podle kterého se provede fulltextové vyhledávání.
     *
     * @returns Akce.
     */
    findAndSelectFeature: (area: string, searchText: string): AnyAction => {
        return FrameActions.postMessage(area, {
            type: 'FEATURE_FIND_AND_SELECT',
            searchText,
        });
    },
};

export default FeatureActions as Readonly<typeof FeatureActions>;
